import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost, getConfigDelete } from "../getConfig";

interface Grant {
  user_id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  from_date: string | null;
  to_date: string | null;
  granted_at: string | null;
  updated_at: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    padding: "1em",
  },
  section: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1em",
  },
  toolbar: {
    display: "flex",
    gap: 16,
    marginBottom: "1em",
    alignItems: "center",
    flexWrap: "wrap",
  },
  tablePaper: {
    width: "100%",
    overflowX: "auto",
  },
  paginationRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1em",
  },
  emptyState: {
    textAlign: "center",
    padding: "3em 1em",
    color: "#666",
  },
}));

const formatDate = (raw: string | null): string => {
  if (!raw) return "—";
  if (raw.startsWith("0000-")) return "—";
  return new Date(raw).toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fullName = (g: Grant): string => {
  const parts = [g.first_name, g.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const MastercardAnywhereAccessDashboard: React.FC = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [grants, setGrants] = useState<Grant[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // Grant modal
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantInput, setGrantInput] = useState("");
  const [granting, setGranting] = useState(false);

  // Revoke confirm
  const [revokeTarget, setRevokeTarget] = useState<Grant | null>(null);
  const [revoking, setRevoking] = useState(false);

  const fetchGrants = useCallback(
    async (page = 1, search = q) => {
      setLoading(true);
      try {
        const params: any = { page, limit: 20 };
        if (search) params.q = search;
        const res: any = await axios.get(
          ENDPOINTS.MC_ANYWHERE_ACCESS,
          getConfigGet(params)
        );
        if (res.data?.status === true) {
          setGrants(res.data.data?.grants || []);
          setPagination(
            res.data.data?.pagination || {
              page: 1,
              limit: 20,
              total: 0,
              total_pages: 0,
            }
          );
        } else {
          notify(res.data?.message || "Failed to load access list", "warning");
        }
      } catch (err: any) {
        notify(
          err?.response?.data?.message || "Failed to load access list",
          "warning"
        );
      } finally {
        setLoading(false);
      }
    },
    [q, notify]
  );

  useEffect(() => {
    fetchGrants(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchGrants(1, q);
  };

  const handleGrant = async () => {
    const value = grantInput.trim();
    if (!value) return;
    setGranting(true);
    try {
      const isNumeric = /^\d+$/.test(value);
      const body = isNumeric
        ? { user_id: parseInt(value, 10) }
        : { email: value };
      const res: any = await axios.post(
        ENDPOINTS.MC_ANYWHERE_ACCESS,
        body,
        { headers: getConfigPost() }
      );
      if (res.data?.status === true) {
        notify(res.data.message || "Access granted", "info");
        setGrantOpen(false);
        setGrantInput("");
        fetchGrants(1);
      } else {
        notify(res.data?.message || "Failed to grant access", "warning");
      }
    } catch (err: any) {
      notify(
        err?.response?.data?.message || "Failed to grant access",
        "warning"
      );
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const res: any = await axios.delete(
        `${ENDPOINTS.MC_ANYWHERE_ACCESS}/${revokeTarget.user_id}`,
        { headers: getConfigDelete() }
      );
      if (res.data?.status === true) {
        notify(res.data.message || "Access revoked", "info");
        setRevokeTarget(null);
        fetchGrants(pagination.page);
      } else {
        notify(res.data?.message || "Failed to revoke access", "warning");
      }
    } catch (err: any) {
      notify(
        err?.response?.data?.message || "Failed to revoke access",
        "warning"
      );
    } finally {
      setRevoking(false);
    }
  };

  const onPrev = () => {
    if (pagination.page > 1) fetchGrants(pagination.page - 1);
  };
  const onNext = () => {
    if (pagination.page < pagination.total_pages) fetchGrants(pagination.page + 1);
  };

  return (
    <div className={classes.main}>
      <div className={classes.section}>
        <div className={classes.header}>
          <Typography variant="h5">Anywhere Card Access</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setGrantOpen(true)}
          >
            Grant Access
          </Button>
        </div>

        <form className={classes.toolbar} onSubmit={handleSearchSubmit}>
          <TextField
            label="Search (email, name, or user_id)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            style={{ minWidth: 320 }}
          />
          <Button type="submit" variant="outlined" disabled={loading}>
            Search
          </Button>
          {q && (
            <Button
              variant="text"
              onClick={() => {
                setQ("");
                fetchGrants(1, "");
              }}
              disabled={loading}
            >
              Clear
            </Button>
          )}
          <Box flexGrow={1} />
          <Typography variant="body2" color="textSecondary">
            {pagination.total} user{pagination.total === 1 ? "" : "s"} granted
          </Typography>
        </form>

        <Paper className={classes.tablePaper}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : grants.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body1">
                {q
                  ? `No grants matching "${q}"`
                  : "No users have access yet. Click 'Grant Access' to add one."}
              </Typography>
            </div>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Granted</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grants.map((g) => (
                    <TableRow key={g.user_id} hover>
                      <TableCell>{g.user_id}</TableCell>
                      <TableCell>{fullName(g)}</TableCell>
                      <TableCell>{g.email}</TableCell>
                      <TableCell>{formatDate(g.granted_at)}</TableCell>
                      <TableCell>{formatDate(g.updated_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Revoke access">
                          <IconButton
                            size="small"
                            onClick={() => setRevokeTarget(g)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className={classes.paginationRow}>
                <Typography variant="body2" color="textSecondary">
                  Page {pagination.page} of {Math.max(pagination.total_pages, 1)}
                </Typography>
                <Box>
                  <IconButton
                    disabled={pagination.page <= 1 || loading}
                    onClick={onPrev}
                    size="small"
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    disabled={
                      pagination.page >= pagination.total_pages || loading
                    }
                    onClick={onNext}
                    size="small"
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              </div>
            </>
          )}
        </Paper>
      </div>

      {/* Grant dialog */}
      <Dialog
        open={grantOpen}
        onClose={() => !granting && setGrantOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Grant Anywhere Card Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the user's email or numeric user ID. They'll see the
            Anywhere card option in the app on next refresh.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email or user_id"
            fullWidth
            value={grantInput}
            onChange={(e) => setGrantInput(e.target.value)}
            disabled={granting}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !granting) {
                e.preventDefault();
                handleGrant();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGrantOpen(false)}
            disabled={granting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGrant}
            color="primary"
            variant="contained"
            disabled={granting || !grantInput.trim()}
          >
            {granting ? <CircularProgress size={20} /> : "Grant"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => !revoking && setRevokeTarget(null)}
        maxWidth="sm"
      >
        <DialogTitle>Revoke access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will hide the Anywhere card feature for{" "}
            <strong>{revokeTarget?.email}</strong> (user_id{" "}
            {revokeTarget?.user_id}). Their existing cards, balances, and
            history are NOT affected — only new purchases are blocked.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            onClick={handleRevoke}
            color="secondary"
            variant="contained"
            disabled={revoking}
          >
            {revoking ? <CircularProgress size={20} /> : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default MastercardAnywhereAccessDashboard;
